<template>
  <div class="podesigner">
    <!-- 顶部工具栏 -->
    <div class="top-toolbar">
      <div class="logo">PoDesigner</div>
      <div class="toolbar-center">
        <button @click="undo" :disabled="!canUndo">撤销</button>
        <button @click="redo" :disabled="!canRedo">重做</button>
        <button @click="save">保存</button>
      </div>
      <div class="toolbar-right">
        <button @click="preview" class="preview-btn">预览</button>
      </div>
    </div>

    <div class="designer-body">
      <!-- 左侧工具栏 -->
      <div class="left-sidebar">
        <div class="tool-tabs">
          <div 
            v-for="tab in toolTabs" 
            :key="tab.key"
            :class="['tool-tab', { active: activeTab === tab.key }]"
            @click="activeTab = tab.key"
          >
            <i :class="tab.icon"></i>
            <span>{{ tab.label }}</span>
          </div>
        </div>

        <!-- 工具内容区 -->
        <div class="tool-content">
          <!-- 上传工具 -->
          <div v-if="activeTab === 'upload'" class="tool-panel">
            <h3>图片上传</h3>
            <input 
              type="file" 
              @change="handleFileUpload" 
              accept="image/*"
              multiple
              ref="fileInput"
              style="display: none"
            />
            <button @click="fileInput?.click()" class="upload-btn">
              上传图片
            </button>
          </div>

          <!-- 图库工具 -->
          <div v-if="activeTab === 'gallery'" class="tool-panel">
            <h3>图片素材</h3>
            <div class="gallery-grid">
              <div 
                v-for="image in galleryImages" 
                :key="image.id"
                class="gallery-item"
                @click="addGalleryImage(image)"
              >
                <img :src="image.url" :alt="image.name" />
              </div>
            </div>
          </div>

          <!-- 文字工具 -->
          <div v-if="activeTab === 'text'" class="tool-panel">
            <h3>文字工具</h3>
            <button @click="addText" class="add-text-btn">添加文字</button>
            <div class="text-templates">
              <div 
                v-for="template in textTemplates"
                :key="template.id"
                class="text-template"
                @click="addTextTemplate(template)"
              >
                {{ template.text }}
              </div>
            </div>
          </div>

          <!-- 图层工具 -->
          <div v-if="activeTab === 'layers'" class="tool-panel">
            <h3>图层</h3>
            <div class="layers-list">
              <div 
                v-for="(layer, index) in layers" 
                :key="layer.id"
                :class="['layer-item', { active: layer.id === selectedLayerId }]"
                @click="selectLayer(layer)"
              >
                <span>{{ layer.name }}</span>
                <button @click.stop="deleteLayer(layer)" class="delete-btn">×</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 中央画布区域 -->
      <div class="canvas-area">
        <div class="canvas-toolbar">
          <div class="zoom-controls">
            <button @click="setZoom(0.25)">25%</button>
            <button @click="setZoom(0.5)">50%</button>
            <button @click="setZoom(1)">100%</button>
            <button @click="fitToScreen">适应屏幕</button>
          </div>
        </div>
        <div ref="canvasContainer" class="canvas-container"></div>
      </div>

      <!-- 右侧属性面板 -->
      <div class="right-sidebar">
        <div v-if="selectedElement" class="properties-panel">
          <h3>属性设置</h3>
          
          <!-- 基础属性 -->
          <div class="property-group">
            <h4>位置和大小</h4>
            <div class="property-row">
              <label>X:</label>
              <input v-model.number="elementProps.x" @input="updateElement" type="number" />
            </div>
            <div class="property-row">
              <label>Y:</label>
              <input v-model.number="elementProps.y" @input="updateElement" type="number" />
            </div>
            <div class="property-row">
              <label>宽度:</label>
              <input v-model.number="elementProps.width" @input="updateElement" type="number" />
            </div>
            <div class="property-row">
              <label>高度:</label>
              <input v-model.number="elementProps.height" @input="updateElement" type="number" />
            </div>
          </div>

          <!-- 文字特有属性 -->
          <div v-if="selectedElement.tag === 'Text'" class="property-group">
            <h4>文字属性</h4>
            <div class="property-row">
              <label>内容:</label>
              <input v-model="elementProps.text" @input="updateElement" type="text" />
            </div>
            <div class="property-row">
              <label>字号:</label>
              <input v-model.number="elementProps.fontSize" @input="updateElement" type="number" />
            </div>
            <div class="property-row">
              <label>颜色:</label>
              <input v-model="elementProps.fill" @input="updateElement" type="color" />
            </div>
          </div>
        </div>

        <div v-else class="no-selection">
          <p>请选择一个元素来编辑属性</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { BaseEditor } from '../editor/BaseEditor'
import type { IUI } from 'leafer-editor'

// Props
export interface PoDesignerProps {
  width?: number
  height?: number
  galleryImages?: Array<{ id: string, url: string, name: string }>
  onSave?: (data: any) => void
  onPreview?: (data: any) => void
}

const props = withDefaults(defineProps<PoDesignerProps>(), {
  width: 1200,
  height: 800,
  galleryImages: () => [] as Array<{ id: string, url: string, name: string }>
})

// Emits
const emit = defineEmits<{
  save: [data: any]
  preview: [data: any]
  elementSelected: [element: IUI | null]
}>()

// 响应式数据
const canvasContainer = ref<HTMLElement>()
const fileInput = ref<HTMLInputElement>()
const activeTab = ref('upload')
const selectedLayerId = ref<string>('')
const selectedElement = ref<IUI | null>(null)
const canUndo = ref(false)
const canRedo = ref(false)

let editor: BaseEditor | null = null

// 工具栏标签
const toolTabs = [
  { key: 'upload', label: '上传', icon: 'icon-upload' },
  { key: 'gallery', label: '图库', icon: 'icon-gallery' },
  { key: 'text', label: '文字', icon: 'icon-text' },
  { key: 'layers', label: '图层', icon: 'icon-layers' }
]

// 图层列表
const layers = ref<Array<{ id: string, name: string, type: string }>>([])

// 文字模板
const textTemplates = [
  { id: '1', text: '标题文字', fontSize: 24, fill: '#000000' },
  { id: '2', text: '正文内容', fontSize: 16, fill: '#333333' },
  { id: '3', text: '装饰文字', fontSize: 18, fill: '#666666' }
]

// 选中元素的属性
const elementProps = reactive({
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  text: '',
  fontSize: 16,
  fill: '#000000'
})

// 初始化编辑器
onMounted(async () => {
  await nextTick()
  if (canvasContainer.value) {
    editor = new BaseEditor({
      container: canvasContainer.value,
      width: props.width,
      height: props.height,
      enableGuideLines: true,
      enableRuler: true
    })
    
    editor.init()
    setupEventListeners()
  }
})

// 销毁编辑器
onUnmounted(() => {
  editor?.destroy()
})

// 设置事件监听
function setupEventListeners() {
  if (!editor) return

  // 监听元素选择
  editor.app.editor.on('select', (e: any) => {
    selectedElement.value = e.list[0] || null
    updateElementProps()
    emit('elementSelected', selectedElement.value)
  })

  // 监听图层变化
  watch(() => editor?.getAllElements(), (elements) => {
    layers.value = elements?.map((el, index) => ({
      id: el.id || `layer-${index}`,
      name: el.tag === 'Text' ? `文字 ${index + 1}` : `图片 ${index + 1}`,
      type: el.tag || 'unknown'
    })) || []
  }, { deep: true })
}

// 更新选中元素属性
function updateElementProps() {
  if (!selectedElement.value) return

  elementProps.x = selectedElement.value.x || 0
  elementProps.y = selectedElement.value.y || 0
  elementProps.width = selectedElement.value.width || 0
  elementProps.height = selectedElement.value.height || 0
  
  if (selectedElement.value.tag === 'Text') {
    elementProps.text = (selectedElement.value as any).text || ''
    elementProps.fontSize = (selectedElement.value as any).fontSize || 16
    elementProps.fill = (selectedElement.value as any).fill || '#000000'
  }
}

// 更新元素
function updateElement() {
  if (!selectedElement.value) return

  Object.assign(selectedElement.value, {
    x: elementProps.x,
    y: elementProps.y,
    width: elementProps.width,
    height: elementProps.height
  })

  if (selectedElement.value.tag === 'Text') {
    Object.assign(selectedElement.value, {
      text: elementProps.text,
      fontSize: elementProps.fontSize,
      fill: elementProps.fill
    })
  }
}

// 文件上传处理
function handleFileUpload(event: Event) {
  const files = (event.target as HTMLInputElement).files
  if (!files || !editor) return

  Array.from(files).forEach(file => {
    const url = URL.createObjectURL(file)
    editor?.addImage(url, {
      x: Math.random() * 300,
      y: Math.random() * 300
    })
  })
}

// 添加图库图片
function addGalleryImage(image: { id: string, url: string, name: string }) {
  if (!editor) return
  
  editor.addImage(image.url, {
    x: Math.random() * 300,
    y: Math.random() * 300
  })
}

// 添加文字
function addText() {
  if (!editor) return
  
  editor.addText('双击编辑文字', {
    x: Math.random() * 300,
    y: Math.random() * 300,
    fontSize: 16,
    fill: '#000000'
  })
}

// 添加文字模板
function addTextTemplate(template: any) {
  if (!editor) return
  
  editor.addText(template.text, {
    x: Math.random() * 300,
    y: Math.random() * 300,
    fontSize: template.fontSize,
    fill: template.fill
  })
}

// 选择图层
function selectLayer(layer: any) {
  selectedLayerId.value = layer.id
  // TODO: 选中对应的编辑器元素
}

// 删除图层
function deleteLayer(layer: any) {
  // TODO: 删除对应的编辑器元素
}

// 设置缩放
function setZoom(zoom: number) {
  editor?.setZoom(zoom)
}

// 适应屏幕
function fitToScreen() {
  // TODO: 实现适应屏幕功能
}

// 撤销重做
function undo() {
  // TODO: 实现撤销功能
}

function redo() {
  // TODO: 实现重做功能
}

// 保存
function save() {
  const data = {
    elements: editor?.getAllElements(),
    // 其他设计数据
  }
  emit('save', data)
}

// 预览
function preview() {
  const data = {
    elements: editor?.getAllElements(),
    // 其他设计数据
  }
  emit('preview', data)
}
</script>

<style scoped>
.podesigner {
  display: flex;
  flex-direction: column;
  height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.top-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.logo {
  font-weight: bold;
  font-size: 18px;
  color: #333;
}

.toolbar-center {
  display: flex;
  gap: 8px;
}

.toolbar-center button, .preview-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.toolbar-center button:hover, .preview-btn:hover {
  background: #f0f0f0;
}

.toolbar-center button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.preview-btn {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.preview-btn:hover {
  background: #0056b3;
}

.designer-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.left-sidebar {
  width: 280px;
  background: #f8f9fa;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
}

.tool-tabs {
  display: flex;
  border-bottom: 1px solid #e9ecef;
}

.tool-tab {
  flex: 1;
  padding: 12px 8px;
  text-align: center;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  font-size: 12px;
}

.tool-tab.active {
  background: white;
  border-bottom-color: #007bff;
}

.tool-tab i {
  display: block;
  margin-bottom: 4px;
  font-size: 16px;
}

.tool-content {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.tool-panel h3 {
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.upload-btn, .add-text-btn {
  width: 100%;
  padding: 8px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.upload-btn:hover, .add-text-btn:hover {
  background: #0056b3;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 8px;
}

.gallery-item {
  aspect-ratio: 1;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
}

.gallery-item:hover {
  border-color: #007bff;
}

.gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.text-templates {
  margin-top: 16px;
}

.text-template {
  padding: 8px;
  margin-bottom: 8px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.text-template:hover {
  border-color: #007bff;
}

.layers-list {
  max-height: 300px;
  overflow-y: auto;
}

.layer-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  margin-bottom: 4px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.layer-item.active {
  border-color: #007bff;
  background: #f0f8ff;
}

.delete-btn {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 2px;
  width: 20px;
  height: 20px;
  font-size: 12px;
  cursor: pointer;
}

.canvas-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f0f0f0;
}

.canvas-toolbar {
  padding: 8px 16px;
  background: white;
  border-bottom: 1px solid #e9ecef;
}

.zoom-controls {
  display: flex;
  gap: 8px;
}

.zoom-controls button {
  padding: 4px 8px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.zoom-controls button:hover {
  background: #f0f0f0;
}

.canvas-container {
  flex: 1;
  background: white;
  margin: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.right-sidebar {
  width: 280px;
  background: #f8f9fa;
  border-left: 1px solid #e9ecef;
  padding: 16px;
  overflow-y: auto;
}

.properties-panel h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.property-group {
  margin-bottom: 24px;
}

.property-group h4 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #666;
}

.property-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
}

.property-row label {
  width: 60px;
  color: #666;
}

.property-row input {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.no-selection {
  text-align: center;
  color: #666;
  margin-top: 50px;
}
</style> 